import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getRewardEligibility } from "../services/rewardService";
import { WORK_NATURE_LABELS } from "../enums/workNature";
import { SkeletonRows } from "../components/ui/Skeleton";
import "../assets/css/pages/rewards.css";

// Disqualification reason -> chip label.
const REASON_LABELS = {
  late: "Late sign-in",
  "short-hours": "Under hours",
  absent: "Excess absence",
};

const fmtDeadline = (t) => (t ? moment(t, "HH:mm:ss").format("h:mm a") : "—");

const Rewards = () => {
  const navigate = useNavigate();
  const goToProfile = (id) => id && navigate(`/members/${id}`);

  const [month, setMonth] = useState(moment().format("YYYY-MM"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("All"); // All | Eligible | Eliminated

  const load = async (m) => {
    setLoading(true);
    const res = await getRewardEligibility(m);
    setData(res);
    setLoading(false);
  };

  useEffect(() => { load(month); }, [month]);

  const shiftMonth = (delta) => setMonth((m) => moment(m, "YYYY-MM").add(delta, "month").format("YYYY-MM"));
  const isCurrentMonth = month === moment().format("YYYY-MM");

  const rows = useMemo(() => {
    if (!data) return [];
    // Backend returns a single ranked list (eligible-by-name first, then eliminated
    // ordered by fewest rules broken) with a continuous `serial`. Tabs only filter;
    // each row keeps its serial from the full ranking.
    const all = data.ranked || [];
    if (tab === "Eligible") return all.filter((r) => !r.eliminated);
    if (tab === "Eliminated") return all.filter((r) => r.eliminated);
    return all;
  }, [data, tab]);

  const counts = data?.counts || { officeStaff: 0, eligible: 0, eliminated: 0 };
  const rules = data?.rules || {};

  return (
    <div>
      {/* KPI strip */}
      <div className="hist-kpis">
        <div className="hkpi"><div className="l">Eligible</div><div className="v" style={{ color: "#34D3A6" }}>{loading ? "…" : counts.eligible}</div><div className="s">win the contest</div></div>
        <div className="hkpi"><div className="l">Eliminated</div><div className="v" style={{ color: "var(--pink)" }}>{loading ? "…" : counts.eliminated}</div><div className="s">broke a rule</div></div>
        <div className="hkpi"><div className="l">Office staff</div><div className="v" style={{ color: "var(--office)" }}>{loading ? "…" : counts.officeStaff}</div><div className="s">in the pool</div></div>
        <div className="hkpi"><div className="l">Sign-in by</div><div className="v" style={{ color: "var(--brand)" }}>{loading ? "…" : fmtDeadline(rules.signinDeadline)}</div><div className="s">later = out</div></div>
        <div className="hkpi"><div className="l">Rules</div><div className="v" style={{ fontSize: 15 }}>{loading ? "…" : `≥${rules.minHours ?? 8}h · ≤${rules.maxAbsentDays ?? 2} absent`}</div><div className="s">per working day / month</div></div>
      </div>

      <div className="card" style={{ marginTop: 16, overflow: "visible" }}>
        <div className="hist-head">
          <div className="filter-wrap" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            {/* Month stepper */}
            <div className="rwd-month">
              <button className="rwd-monthnav" onClick={() => shiftMonth(-1)} aria-label="Previous month"><svg><use href="#i-chev-l" /></svg></button>
              <input type="month" className="rwd-monthinput" value={month} max={moment().format("YYYY-MM")} onChange={(e) => e.target.value && setMonth(e.target.value)} />
              <button className="rwd-monthnav" onClick={() => shiftMonth(1)} disabled={isCurrentMonth} aria-label="Next month"><svg><use href="#i-chev-r" /></svg></button>
            </div>
            <div className="hist-tabs">
              {["All", "Eligible", "Eliminated"].map((t) => (
                <button key={t} className={`hist-tab ${tab === t ? "on" : ""}`} onClick={() => setTab(t)}>{t}</button>
              ))}
            </div>
          </div>
          <div className="rwd-monthlabel">{moment(month, "YYYY-MM").format("MMMM YYYY")}</div>
        </div>

        {/* Header + rows scroll together on narrow screens. */}
        <div className="table-scroll">
        <div className="rwd-grid rwd-colhead">
          <div>#</div><div>Member</div><div>Nature</div><div>Present</div><div>Late</div><div>Under 8h</div><div>Absent</div><div>Status</div>
        </div>

        {loading && <SkeletonRows count={8} />}
        {!loading && rows.length === 0 && <div className="hist-empty">No office staff for this month.</div>}

        {!loading && rows.map((r) => (
          <div className="rwd-grid rwd-row" key={r.memberId}>
            <div className="rwd-serial">{r.serial}</div>
            <div className="hist-mem" role="button" tabIndex={0} title="View profile" onClick={() => goToProfile(r.memberId)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToProfile(r.memberId)}>
              {r.avatar ? <img className="av av-30" src={r.avatar} alt="" style={{ objectFit: "cover" }} /> : <div className="av av-30 av-MH">{(r.name || "?").slice(0, 2).toUpperCase()}</div>}
              <div style={{ minWidth: 0 }}><div className="log-name">{r.displayName || r.name}</div><div className="log-role">{r.title || ""}</div></div>
            </div>
            <div><span className="pill-type" style={{ color: "var(--office)", background: "var(--office-bg)" }}><svg><use href="#i-building" /></svg>{WORK_NATURE_LABELS[r.workNature] || r.workNature}</span></div>
            <div className="rwd-num">{r.presentDays}</div>
            <div className={`rwd-num ${r.lateDays > 0 ? "bad" : ""}`}>{r.lateDays}</div>
            <div className={`rwd-num ${r.shortDays > 0 ? "bad" : ""}`}>{r.shortDays}</div>
            <div className={`rwd-num ${r.absentDays > (rules.maxAbsentDays ?? 2) ? "bad" : ""}`}>{r.absentDays}</div>
            <div className="rwd-status">
              {r.eliminated ? (
                <div className="rwd-reasons">
                  <span className="status-pill absent">Eliminated</span>
                  {r.reasons.map((rs) => <span key={rs} className="rwd-reason-chip">{REASON_LABELS[rs] || rs}</span>)}
                </div>
              ) : (
                <span className="status-pill present">Eligible</span>
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default Rewards;
